import { simulateContract } from 'wagmi/actions';
import { encodeFunctionData, type Abi, type Hex } from 'viem';
import type { Config } from 'wagmi';

interface PrepareContractWriteParams<
  TAbi extends Abi,
  TFunctionName extends string,
  TArgs extends readonly unknown[] | undefined = undefined,
> {
  config: Config;
  address: Hex;
  abi: TAbi;
  functionName: TFunctionName;
  args: TArgs;
  account: Hex | undefined;
  chainId: number | undefined;
}

type WriteArgs<TFunctionName extends string, TArgs extends readonly unknown[] | undefined> = TArgs extends undefined
  ? { address: Hex; functionName: TFunctionName; chainId: number }
  : { address: Hex; functionName: TFunctionName; args: TArgs; chainId: number };

interface PreparedContractWrite<TFunctionName extends string, TArgs extends readonly unknown[] | undefined = undefined> {
  /** The encoded transaction data for revert decoding */
  transactionData: Hex;
  /** The arguments to pass to the write function */
  writeArgs: WriteArgs<TFunctionName, TArgs>;
}

/**
 * Prepares a contract write by simulating and encoding the transaction
 * This consolidates the common pattern of:
 * 1. Simulating the contract call
 * 2. Encoding the function data for revert decoding
 * 3. Preparing arguments for the write function
 *
 * @param params Contract write parameters
 * @returns Prepared write data including encoded transaction data
 * @throws Error if simulation fails
 *
 * @example
 * ```ts
 * const prepared = await prepareContractWrite({
 *   config,
 *   address: exchangeAddress,
 *   abi: dexAbi,
 *   functionName: 'withdrawCollateral',
 *   args: [amount],
 *   account,
 *   chainId: 1
 * });
 *
 * setTransactionData(prepared.transactionData);
 * writeWithdraw(prepared.writeArgs);
 * ```
 */
export async function prepareContractWrite<
  TAbi extends Abi,
  TFunctionName extends string,
  TArgs extends readonly unknown[] | undefined = undefined,
>(params: PrepareContractWriteParams<TAbi, TFunctionName, TArgs>): Promise<PreparedContractWrite<TFunctionName, TArgs>> {
  const { config, address, abi, functionName, args, account, chainId } = params;

  if (!chainId) {
    throw new Error('Chain ID is required for contract interaction');
  }

  await simulateContract(config, {
    address,
    abi,
    functionName,
    args,
    account,
    chainId,
  } as any);

  const transactionData = encodeFunctionData({
    abi,
    functionName,
    args,
  } as any) as Hex;

  const writeArgs = (
    args != null ? { address, functionName, args, chainId } : { address, functionName, chainId }
  ) as PreparedContractWrite<TFunctionName, TArgs>['writeArgs'];

  return {
    transactionData,
    writeArgs,
  };
}
